import { useCallback, useEffect, useRef, useState } from 'react';
import {
  classifyBeerIngestionBrewery,
  excludeBeerIngestionBrewery,
  listBeerIngestion,
} from '../services/beerIngestionService';
import {
  setTrustedBreweryBeerSource,
  upsertBreweryBeerSource,
  updateBreweryBeerSourceStatus,
  runBeerSourceMonitor,
  recreateBeerSourceMonitor,
  enableBeerSourceMonitoring,
} from '../services/breweryBeerSourcesService';
import { EMPTY_BEER_INGESTION_SUMMARY } from '../utils/brewery/beerIngestionStatus';

function useBeerIngestion(stateCode) {
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(EMPTY_BEER_INGESTION_SUMMARY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const requestGenerationRef = useRef(0);

  const refresh = useCallback(async (options = {}) => {
    const keepVisible = options.keepVisible === true;
    const requestGeneration = requestGenerationRef.current + 1;
    requestGenerationRef.current = requestGeneration;

    if (!stateCode) {
      if (requestGeneration !== requestGenerationRef.current) {
        return;
      }
      setRows([]);
      setSummary(EMPTY_BEER_INGESTION_SUMMARY);
      setLoading(false);
      return;
    }

    try {
      if (!keepVisible) {
        setLoading(true);
      }
      setError(null);
      const result = await listBeerIngestion(stateCode);
      if (requestGeneration !== requestGenerationRef.current) {
        return;
      }
      setRows(result.rows);
      setSummary(result.summary);
    } catch (err) {
      if (requestGeneration !== requestGenerationRef.current) {
        return;
      }
      console.error('Error fetching beer ingestion rows:', err);
      setError(err?.message || 'Failed to load beer ingestion rows');
      setRows([]);
      setSummary(EMPTY_BEER_INGESTION_SUMMARY);
    } finally {
      if (requestGeneration === requestGenerationRef.current) {
        setLoading(false);
      }
    }
  }, [stateCode]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const runMutation = useCallback(
    async (mutationFn, nextPendingAction = null) => {
      try {
        setActionLoading(true);
        setPendingAction(nextPendingAction);
        setActionError(null);
        const result = await mutationFn();
        await refresh({ keepVisible: true });
        return result;
      } catch (err) {
        console.error('Beer ingestion mutation failed:', err);
        const message = err?.message || 'Update failed';
        setActionError(message);
        throw err;
      } finally {
        setActionLoading(false);
        setPendingAction(null);
      }
    },
    [refresh],
  );

  const classifyBrewery = useCallback(
    (breweryId, payload) =>
      runMutation(
        () => classifyBeerIngestionBrewery({ breweryId, ...payload }),
        {
          breweryId,
          action:
            payload?.status === 'no_suitable_source' ? 'no-source' : 'clear-no-source',
        },
      ),
    [runMutation],
  );

  const excludeBrewery = useCallback(
    (breweryId, payload) =>
      runMutation(
        () => excludeBeerIngestionBrewery({ breweryId, ...payload }),
        {
          breweryId,
          action: payload?.excluded ? 'exclude' : 'include',
        },
      ),
    [runMutation],
  );

  const addOrUpdateSource = useCallback(
    (breweryId, payload) =>
      runMutation(
        () => upsertBreweryBeerSource({ breweryId, ...payload }),
        { breweryId, action: 'add-source' },
      ),
    [runMutation],
  );

  const markTrusted = useCallback(
    (breweryId, sourceId) =>
      runMutation(
        () => setTrustedBreweryBeerSource(breweryId, sourceId),
        { breweryId, action: 'trust' },
      ),
    [runMutation],
  );

  const updateSourceStatus = useCallback(
    (breweryId, sourceId, monitoringStatus) =>
      runMutation(
        () => updateBreweryBeerSourceStatus(sourceId, monitoringStatus),
        {
          breweryId,
          action: monitoringStatus === 'paused' ? 'pause' : 'resume',
        },
      ),
    [runMutation],
  );

  const checkSourceNow = useCallback(
    (breweryId, sourceId) =>
      runMutation(
        () => runBeerSourceMonitor(sourceId),
        { breweryId, action: 'check-now' },
      ),
    [runMutation],
  );

  const recreateSourceMonitor = useCallback(
    (breweryId, sourceId) =>
      runMutation(
        () => recreateBeerSourceMonitor(sourceId),
        { breweryId, action: 'recreate-monitor' },
      ),
    [runMutation],
  );

  const startSourceMonitoring = useCallback(
    (breweryId, sourceId) =>
      runMutation(
        () => enableBeerSourceMonitoring({ sourceId }),
        { breweryId, action: 'start-monitoring' },
      ),
    [runMutation],
  );

  const enableNhMonitoring = useCallback(
    () =>
      runMutation(
        () => enableBeerSourceMonitoring({ stateCode: 'NH' }),
        { breweryId: 'nh-bulk', action: 'enable-nh-monitoring' },
      ),
    [runMutation],
  );

  return {
    rows,
    summary,
    loading,
    error,
    actionError,
    actionLoading,
    pendingAction,
    refresh,
    classifyBrewery,
    excludeBrewery,
    addOrUpdateSource,
    markTrusted,
    updateSourceStatus,
    checkSourceNow,
    recreateSourceMonitor,
    startSourceMonitoring,
    enableNhMonitoring,
  };
}

export default useBeerIngestion;
